import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import { TextField, Autocomplete, Box, Backdrop, CircularProgress } from '@mui/material';

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, isUndefined } from "lodash";

import itemService from "@plannel/services/data/item-service"
import locationService from "@plannel/services/data/location-service"
import supplierService from "@plannel/services/data/supplier-service"
import supplierItemService from "@plannel/services/data/supplier-item-service"
import materialReceiptPlanService from "@plannel/services/data/material-receipt-plan-service"
import PaginationContainer from "@plannel/components/PaginationContainer";
import Dialog from "@plannel/components/Dialog";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import ItemAutocomplete from "@plannel/components/filter/ItemAutocomplete";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import DefaultGridSetting, { nullSafeComparator } from "@plannel/components/aggrid/DefaultGridSetting";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import GridDatePicker from "@plannel/components/GridDatePicker";
import DateCondition from "@plannel/components/DateCondition";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const MaterialReceiptPlan = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState ? reduxViewState.currentPage : 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState ? reduxViewState.pageSize : pageSizes[0]);

  const [sortParams, setSortParams] = useState({});
  const [locSearch, setLocSearch] = useState(reduxViewState?.locSearch ?? null);
  const [supplierSearch, setSupplierSearch] = useState(reduxViewState?.supplierSearch ?? null);
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [progressOn, setProgressOn] = useState(false);

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const [matlReceitPlan, setMatlReceitPlan] = useState([]);
  const [location, setLocation] = useState([]);
  const [supplier, setSupplier] = useState([]);
  const [item, setItem] = useState([]);
  const CURRENT_DATE = new ZDate();
  const MIN_DATE = new ZDate(CURRENT_DATE.getFullYear() - 10, 0, 1);
  const MAX_DATE = new ZDate(CURRENT_DATE.getFullYear() + 10, 0, 1);
  const START_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), 1);
  const END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + 3, 0);
  const initPeriod = { start: reduxViewState?.period ? new ZDate(reduxViewState.period.start) : START_DATE, end: reduxViewState?.period ? new ZDate(reduxViewState.period.end) : END_DATE }
  const [period, setPeriod] = useState(initPeriod);
  const [selectedSearch, setSelectedSearch] = useState(reduxViewState?.selectedSearch ?? "eta");
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { snackBarMessageColor } = ColorUtil;
  const conditionList = CodeUtil.shipmentCondition;
  const dropdownLocationFormatter = (data) => {
    return data.value ? data.value.name : "";
  }

  const dropdownItemFormatter = (data) => {
    return data.value ? data.value.name : "";
  }

  const dropdownSupplierFormatter = (data) => {
    return data.value ? data.value.name : "";
  }

  const objectComparator = (value1, value2, property, isDescending) => {
    const val1 = !value1 ? "" : value1[property];
    const val2 = !value2 ? "" : value2[property];
    
    return nullSafeComparator(val1, val2, isDescending);
  }

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (supplierSearch) {
      searchFilters.supplierCd = supplierSearch.supplierCd;
    }

    if (locSearch) {
      searchFilters.locCd = locSearch.locCd;
    }

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    if (selectedSearch) {
      searchFilters.condition = selectedSearch
    }

    if (period?.start) {
      searchFilters.startDate = period?.start
    }

    if (period?.end) {
      searchFilters.endDate = period?.end
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  }

  const retrieveMaterialReceiptPlan = () => {
    const params = getRequestParams();
    materialReceiptPlanService.getAll(params)
      .then(response => {
        const { results, totalPages } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }

        setMatlReceitPlan(results);
        setTotalPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { period, selectedSearch, locSearch, supplierSearch, itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, currentPage, pageSize, advancedFilters });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      })
  }

  const retrieveSupplier = (itemCd) => {
    if (itemCd) {
      supplierItemService.getForItem({ item: itemCd })
      .then(response => {
        const { results } = response.data;
        const result = [];
        for (const val of results) {
          result.push(val.supplier)
        }
        setSupplier(result);
      })
      .catch(e => {
        debug(e);
      });
    } else {
      supplierService.getAll()
      .then(response => {
        const { results } = response.data;
        setSupplier(results);
      })
      .catch(e => {
        debug(e);
      });
    }

  };

  const retrieveLocations = () => {
    locationService.getAll({ pagination: false })
      .then(response => {
        const { results } = response.data;
        setLocation(results.filter(result => result.locType === 'PLANT'));
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrieveItems = (supplierCd) => {
    if (supplierCd) {
      supplierItemService.getAll({ searchFilters: { supplierCd } })
      .then(response => {
        const { results } = response.data;
        const itemResult = [];
        for (const val of results) {
          itemResult.push(val.item)
        }
        setItem(itemResult);
      })
      .catch(e => {
        debug(e);
      });
    } else {
      itemService.getItemByType({ itemType: ['RM', 'SFG'] })
      .then(response => {
        const { results } = response.data;
        setItem(results);
      })
      .catch(e => {
        debug(e);
      });
    }
  };

  const insertRow = () => {
    const newRow = {
      id: null,
      locCd: null,
      locName: null,
      itemCd: null,
      itemName: null,
      supplierCd: null,
      supplierName: null,
      baseDate: null,
      orderNum: null,
      orderQty: null,
      orderAmt: null,
      estimateDeparture: null,
      actualDeparture: null,
      estimateArrival: null,
      actualArrival: null,
      descTxt: null,
      verNum: null
    };
    retrieveItems();
    retrieveSupplier();
    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  }

  
  const onCellValueChangedGroup = (params) => {
    if (params.colDef.field === "supplier") {
      retrieveItems(params.data.supplier.supplierCd);
    } else if (params.colDef.field === "item") {
      retrieveSupplier(params.data.item.itemCd);
    }
  }

  const columnDefs = [
    {
      headerName: "locCd",
      field: "location.locCd",
      colId: "locCd",
      minWidth: 150,
      type: ["nonEditableColumn"],
      flex: 2,
      cellClass: 'stringType', filterType: 'string'
    },
    {
      headerName: "locName", field: "location", colId: "locName",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: dropdownLocationFormatter,
      cellEditorParams: {
        values: reduxViewState?.location ?? location
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
      width: 150, filterType: 'string'
    },
    {
      headerName: "itemCd",
      field: "item.itemCd",
      colId: "itemCd",
      minWidth: 150,
      type: ["nonEditableColumn"],
      flex: 2,
      cellClass: 'stringType', filterType: 'string'
    },
    {
      headerName: "itemName", field: "item", colId: "itemName",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: dropdownItemFormatter,
      cellEditorParams: {
        values: reduxViewState?.item ?? item
      },
      onCellValueChanged: onCellValueChangedGroup,
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
      width: 150, 
      filterType: 'string',
    },
    {
      headerName: "supplierCd",
      field: "supplier.supplierCd",
      colId: "supplierCd",
      minWidth: 150,
      type: ["nonEditableColumn"],
      flex: 2,
      cellClass: 'stringType', filterType: 'string'
    },
    {
      headerName: "supplierName", field: "supplier", colId: "supplierName",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: dropdownSupplierFormatter,
      cellEditorParams: {
        values: reduxViewState?.supplier ?? supplier
      },
      onCellValueChanged: onCellValueChangedGroup,
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
      width: 150, 
      filterType: 'string',
    },
    { headerName: "baseDt", field: "baseDate", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 120, filterType: 'date', valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "orderNum", field: "orderNum", width: 200, filterType: 'string' },
    { headerName: "orderDate", field: "orderDate", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 120, filterType: 'date', valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "orderQty", field: "orderQty", type: ["numberColumn", "rightAligned"], width: 120, filterType: 'number' },
    { headerName: "orderAmt", field: "orderAmt", type: ["numberColumn", "rightAligned"], width: 120, filterType: 'number'},
    { headerName: "etd", field: "estimateDeparture", colId: "etd", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 120, filterType: 'date', valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "atd", field: "actualDeparture", colId: "atd", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 120, filterType: 'date', valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "eta", field: "estimateArrival", colId: "eta", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 120, filterType: 'date', valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "ata", field: "actualArrival", colId: "ata", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 120, filterType: 'date', valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "descTxt", field: "descTxt", width: 200, filterType: 'string' },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: 'timestamp' },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' }
  ];

  const removeMatlReceiptPlan = () => {
    const routeIds = [];

    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        routeIds.push(data.id)
      }
    );
    if (routeIds.length > 0) {
      materialReceiptPlanService.remove(routeIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
        })
        .then(() => {
          retrieveMaterialReceiptPlan();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const saveMatlReceiptPlan = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    if (allStateData.length > 0) {
      materialReceiptPlanService.upsert(allStateData)
        .then((response) => {
          if (response.data?.message) {
            const { message } = response.data;
            setSnackInfo({ open: true, severity: message.type, content: t(message.message)});
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
          }
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
        .then(() => {
          setProgressOn(false);
          retrieveMaterialReceiptPlan();
        });
    } else {
      setProgressOn(false)
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      setProgressOn(true)
      saveMatlReceiptPlan();
    } else if (action === "DELETE") {
      removeMatlReceiptPlan();
    }
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveMaterialReceiptPlan();
    }
  }
 
  const onGridReady = params => {
    retrieveLocations();
    retrieveItems();
    retrieveSupplier();
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };


  useEffect(() => {
    if (!isUndefined(item) && !isEmpty(item)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  useEffect(() => {
    if (!isUndefined(location) && !isEmpty(location)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (!isUndefined(supplier) && !isEmpty(supplier)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplier]);

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveMaterialReceiptPlan();
    }

    return () => {
      setMatlReceitPlan([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }
    return () => {
      setMatlReceitPlan([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, locSearch, supplierSearch]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }
    return () => {
      setMatlReceitPlan([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, selectedSearch]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  return (
    <>
      <FilterContainer>
        <Box id="material-receipt-plan-left">
        <DateCondition
          conditionList={conditionList}
          period={period}
          maxDate={MAX_DATE}
          minDate={MIN_DATE}
          selectedSearch={selectedSearch}
          setSelectedSearch={setSelectedSearch}
          setPeriod={setPeriod}
          useAutoComplete={false}
          views={['year', 'month']}
        />
        </Box>
        <Autocomplete
          options={location}
          getOptionLabel={option => option.name}
          value={locSearch}
          onChange={(_, newValue) => setLocSearch(newValue)}
          isOptionEqualToValue={(option, value) => option.locCd === value.locCd}
          size="small"
          sx={{ minWidth: 210, mr: 1 }}
          renderInput={(params) => (
            <TextField {...params} label={t("location")} />
          )}
        />
        <Autocomplete
          options={supplier}
          getOptionLabel={option => option.name}
          value={supplierSearch}
          onChange={(_, newValue) => setSupplierSearch(newValue)}
          isOptionEqualToValue={(option, value) => option.supplierCd === value.supplierCd}
          size="small"
          sx={{ minWidth: 210, mr: 1 }}
          renderInput={(params) => (
            <TextField {...params} label={t("supplier")} />
          )}
        />
        <ItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} useAccessFilter={false} extraCodesRef={extraItemCodesRef} />
        <Box sx={{ ml: "auto", minWidth: "140px" }}>
          <ExcelExportButton title={title} service={materialReceiptPlanService} params={getRequestParams()} columnDefs={columnDefs} />
          <AddButton onClick={insertRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box id="material-receipt-plan-grid" className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={matlReceitPlan}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              onGridReady={onGridReady}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Backdrop sx={{ color: '#fff', zIndex: 10000 }} open={progressOn}>
        <CircularProgress color="inherit" size={"80px"} />
      </Backdrop>
    </>
  );
};

export default withTranslation()(MaterialReceiptPlan);